
// Welcome.jsx
// Splash screen for first-time users – displayed before Dashboard
// Updated to match new bundle‑centric economics (July 2026)
// Plan value now shows a range based on the target suggested output
// Video rates: $75–$150 per short‑form motion clip
// 10 iterations = 1 final image
// Shared generation budget – iterations & videos draw from a single pool

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiInfo } from 'react-icons/fi';
import { RiImageCircleAiLine, RiVideoOnAiLine } from 'react-icons/ri';
import { GiPowerButton } from 'react-icons/gi';
import LogoWhite from '../assets/LOGO_WHITE.png';
import DefaultAvatarIcon from '../assets/avataricon.jpg';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import {
  IMAGE_COST_CREDITS,
  VIDEO_COST_CREDITS,
  ITERATIONS_PER_FINAL_IMAGE,
  getUserPlanCaps,
  TARGET_BUNDLES,
  IMAGE_MARKET_RATE,
  VIDEO_MARKET_RATE_LOW,
  VIDEO_MARKET_RATE_HIGH,
  getPlanValueRange,
} from '../services/usageService';

// ------------------------------------------------------------
// Face order – maps logical face → physical cube face
// ------------------------------------------------------------
const FACE_ORDER = ['front', 'right', 'back', 'left'];

const FACE_LABELS = {
  front: 'Welcome',
  right: 'Real World Value',
  back: 'How it Works',
  left: 'Done',
};

const FACE_ROTATIONS = {
  front: 0,
  right: 90,
  back: 180,
  left: 270,
};

// ------------------------------------------------------------
// Timing & visual configuration – adjust these values as needed
// ------------------------------------------------------------
const BORDER_HIGHLIGHT_DURATION = 500;
const BORDER_FADE_DURATION = 200;
const BORDER_HIGHLIGHT_COLOR = '#65a3ffff';

const FLICKER_OVERLAY_DURATION = 800;
const FLICKER_DELAY_BEFORE_ROTATION = 400;

const ROTATION_TRANSITION_DURATION = 800;
const ROTATION_SETTLE_TIME = 800;

const POST_ROTATION_BLINK_DELAY = 100;
const PULSE_COLOR_WHITE = 'rgba(255,255,255,0.85)';
const PULSE_COLOR_BLUE = '#65a3ffff';
const BLUE_HOLD_MS = 700;
const WHITE_HOLD_MS = 300;

const LOGO_X_OFFSET = -15;
const AVATAR_X_OFFSET = -10;

const TOTAL_SEQUENCE_DURATION =
  BORDER_HIGHLIGHT_DURATION +
  BORDER_FADE_DURATION +
  FLICKER_DELAY_BEFORE_ROTATION +
  ROTATION_TRANSITION_DURATION +
  ROTATION_SETTLE_TIME;
const CHEVRON_FADE_IN_DELAY = Math.round(TOTAL_SEQUENCE_DURATION * 0.5); // ≈1350 ms

// ------------------------------------------------------------
// Keyboard navigation toggle – set to false to disable arrow keys
// ------------------------------------------------------------
const ENABLE_KEYBOARD_NAVIGATION = false;

// ------------------------------------------------------------
// Main Component
// ------------------------------------------------------------
export default function WelcomeScreen({ onComplete }) {
  const navigate = useNavigate();
  const auth = getAuth();

  // ---- Firebase Auth & Firestore state ----
  const [authUser, setAuthUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // ---- UI state ----
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);

  const [rotateY, setRotateY] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [activeFace, setActiveFace] = useState('front');
  const [targetFace, setTargetFace] = useState('front');

  const [rockOffset, setRockOffset] = useState(0);
  const rockAnimationRef = useRef(null);
  const rockStartTimeRef = useRef(null);

  const [flickeringFace, setFlickeringFace] = useState(null);
  const [borderHighlightFace, setBorderHighlightFace] = useState(null);

  const [showDollarValue, setShowDollarValue] = useState(false);
  const dollarToggleIntervalRef = useRef(null);

  const [rotationInProgress, setRotationInProgress] = useState(false);
  const [blinkSide, setBlinkSide] = useState('none');
  const [pulseColor, setPulseColor] = useState('white');

  const [chevronsVisible, setChevronsVisible] = useState(true);

  const blinkSequenceRef = useRef(null);
  const blinkStartTimeoutRef = useRef(null);
  const chevronFadeInTimeoutRef = useRef(null);

  const [cubeSize, setCubeSize] = useState(0);

  const rotationTimeoutRef = useRef(null);
  const flickerClearTimeoutRef = useRef(null);
  const borderHighlightTimeoutRef = useRef(null);
  const borderFadeTimeoutRef = useRef(null);
  const dragStart = useRef({ x: 0, y: 0 });

  // ---------- Modal states ----------
  const [isConversionModalOpen, setIsConversionModalOpen] = useState(false);
  const [isSlide3InfoOpen, setIsSlide3InfoOpen] = useState(false);
  const [popupShowDollar, setPopupShowDollar] = useState(false);

  // ---- Subscribe to auth state and Firestore ----
  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
      if (user) {
        try {
          const userRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(userRef);
          if (docSnap.exists()) {
            setUserData(docSnap.data());
          } else {
            setUserData({ uid: user.uid, email: user.email });
          }
        } catch (error) {
          console.error('Error fetching user data in Welcome:', error);
        }
        const unsubscribeSnapshot = onSnapshot(doc(db, 'users', user.uid), (snap) => {
          if (snap.exists()) {
            setUserData(snap.data());
          }
        });
        setIsLoading(false);
        return () => {
          unsubscribeSnapshot();
        };
      } else {
        setAuthUser(null);
        setUserData(null);
        setIsLoading(false);
        navigate('/');
      }
    });

    return () => unsubscribeAuth();
  }, [auth, navigate]);

  // ---- Derived data (centralised via usageService) ----
  const caps = useMemo(() => getUserPlanCaps(userData), [userData]);
  const planName = caps.name || 'Starter';
  const iterationCap = caps.iterationCap || 77;
  const videoCap = caps.videoCap || 2;
  const genBudget = caps.genBudget || 5.04;
  const planCredits = Math.round(genBudget * 100);

  const usedIterations = useMemo(() => {
    return Number(userData?.usage?.iterationsGenerated ?? 0) || 0;
  }, [userData]);
  const usedVideos = useMemo(() => {
    return Number(userData?.usage?.videosGenerated ?? 0) || 0;
  }, [userData]);
  const remainingIterations = Math.max(0, iterationCap - usedIterations);
  const remainingVideos = Math.max(0, videoCap - usedVideos);

  const bundle = useMemo(
    () => TARGET_BUNDLES[planName.toLowerCase()] || TARGET_BUNDLES.starter,
    [planName]
  );
  const planValueRange = useMemo(() => getPlanValueRange(planName), [planName]);
  const planValueStr = `$${planValueRange.low.toLocaleString()} – $${planValueRange.high.toLocaleString()}`;

  const billingCycle =
    userData?.subscription?.billingCycle || userData?.pendingPlan?.billingCycle || 'monthly';
  const billingLabel =
    billingCycle === 'annual' || billingCycle === 'yearly' ? 'Annual' : 'Monthly';
  const displayName =
    userData?.displayName || userData?.firstName || authUser?.email?.split('@')[0] || 'User';

  const perIterationCost = (IMAGE_COST_CREDITS * 0.01).toFixed(3);
  const perFinalImageCost = (IMAGE_COST_CREDITS * ITERATIONS_PER_FINAL_IMAGE * 0.01).toFixed(2);
  const perVideoCost = (VIDEO_COST_CREDITS * 0.01).toFixed(2);

  const avatarUrl = useMemo(() => {
    if (userData?.photoURL) return userData.photoURL;
    if (authUser?.photoURL) return authUser.photoURL;
    return DefaultAvatarIcon;
  }, [userData, authUser]);

  // ---- Responsive detection ----
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1224);
      setIsDesktop(width >= 1224);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    setRotateY(0);
    setActiveFace('front');
  }, [isMobile]);

  // ---- Dollar value toggle (Slide 3) ----
  useEffect(() => {
    if (dollarToggleIntervalRef.current) clearInterval(dollarToggleIntervalRef.current);
    setShowDollarValue(false);
    dollarToggleIntervalRef.current = setInterval(() => {
      setShowDollarValue(prev => !prev);
    }, 2000);
    return () => {
      if (dollarToggleIntervalRef.current) clearInterval(dollarToggleIntervalRef.current);
    };
  }, [activeFace]);

  // ---- Dollar value toggle for popup ----
  useEffect(() => {
    if (!isSlide3InfoOpen) {
      setPopupShowDollar(false);
      return;
    }
    setPopupShowDollar(false);
    const interval = setInterval(() => {
      setPopupShowDollar(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, [isSlide3InfoOpen]);

  // ---- Rock animation ----
  useEffect(() => {
    if (isTransitioning) {
      setRockOffset(0);
      return;
    }
    rockStartTimeRef.current = null;
    const rockAmount = 9;
    const rockDuration = 4000;
    const animateRock = (timestamp) => {
      if (!rockStartTimeRef.current) rockStartTimeRef.current = timestamp;
      const elapsed = timestamp - rockStartTimeRef.current;
      const progress = (elapsed % rockDuration) / rockDuration;
      const offset = Math.sin(progress * Math.PI * 2) * rockAmount;
      setRockOffset(offset);
      rockAnimationRef.current = requestAnimationFrame(animateRock);
    };
    rockAnimationRef.current = requestAnimationFrame(animateRock);
    return () => {
      if (rockAnimationRef.current) cancelAnimationFrame(rockAnimationRef.current);
    };
  }, [isTransitioning]);

  // ---- Cube size ----
  // Fully immersive: use the full viewport height
  const BOX_HEIGHT_RATIO = 1;
  const SQUARE_FACTOR = isDesktop ? 1.0 : (isMobile ? 0.9 : 0.95);
  const PERSPECTIVE = isMobile ? 250 : 500;

  useEffect(() => {
    const updateDims = () => {
      const h = window.innerHeight;
      const boxAreaHeight = h * BOX_HEIGHT_RATIO;
      const squareSize = Math.min(
        window.innerWidth * SQUARE_FACTOR,
        boxAreaHeight * SQUARE_FACTOR
      );
      setCubeSize(squareSize);
    };
    updateDims();
    window.addEventListener('resize', updateDims);
    return () => window.removeEventListener('resize', updateDims);
  }, [isMobile, isDesktop, isTablet, BOX_HEIGHT_RATIO, SQUARE_FACTOR]);

  // ---- Rotation logic ----
  const getActiveFaceFromRotation = useCallback((ry) => {
    const y = ((ry % 360) + 360) % 360;
    if (y >= 315 || y < 45) return 'front';
    if (y >= 45 && y < 135) return 'right';
    if (y >= 135 && y < 225) return 'back';
    return 'left';
  }, []);

  const performRotation = useCallback(
    (targetY) => {
      if (isTransitioning) return;
      setIsTransitioning(true);
      const normalizedY = Math.round(targetY / 90) * 90;
      const targetFaceName = getActiveFaceFromRotation(normalizedY);
      setTargetFace(targetFaceName);
      setBorderHighlightFace(null);
      setFlickeringFace(targetFaceName);
      if (flickerClearTimeoutRef.current) clearTimeout(flickerClearTimeoutRef.current);
      if (rotationTimeoutRef.current) clearTimeout(rotationTimeoutRef.current);
      flickerClearTimeoutRef.current = setTimeout(() => {
        setFlickeringFace(null);
      }, FLICKER_OVERLAY_DURATION);
      rotationTimeoutRef.current = setTimeout(() => {
        setRotateY(normalizedY);
        const finaliseTimeout = setTimeout(() => {
          setIsTransitioning(false);
          setActiveFace(targetFaceName);
          setRotationInProgress(false);
          rotationTimeoutRef.current = null;
        }, ROTATION_SETTLE_TIME);
        rotationTimeoutRef.current = finaliseTimeout;
      }, FLICKER_DELAY_BEFORE_ROTATION);
    },
    [isTransitioning, getActiveFaceFromRotation]
  );

  const startBorderHighlightSequence = useCallback((targetY) => {
    if (isTransitioning) return;
    setBlinkSide('none');
    setRotationInProgress(true);
    setChevronsVisible(false);
    setPulseColor('white');
    if (blinkSequenceRef.current) clearTimeout(blinkSequenceRef.current);
    if (chevronFadeInTimeoutRef.current) clearTimeout(chevronFadeInTimeoutRef.current);
    chevronFadeInTimeoutRef.current = setTimeout(() => {
      setChevronsVisible(true);
    }, CHEVRON_FADE_IN_DELAY);
    if (borderHighlightTimeoutRef.current) clearTimeout(borderHighlightTimeoutRef.current);
    if (borderFadeTimeoutRef.current) clearTimeout(borderFadeTimeoutRef.current);
    const targetFaceName = getActiveFaceFromRotation(Math.round(targetY / 90) * 90);
    setBorderHighlightFace(targetFaceName);
    borderHighlightTimeoutRef.current = setTimeout(() => {
      setBorderHighlightFace(null);
      borderFadeTimeoutRef.current = setTimeout(() => {
        performRotation(targetY);
      }, BORDER_FADE_DURATION);
    }, BORDER_HIGHLIGHT_DURATION);
  }, [isTransitioning, getActiveFaceFromRotation, performRotation]);

  const navigateToFace = useCallback((faceIndex) => {
    const targetFaceKey = FACE_ORDER[faceIndex];
    const targetRotation = FACE_ROTATIONS[targetFaceKey];
    const currentY = ((rotateY % 360) + 360) % 360;
    let delta = targetRotation - currentY;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    startBorderHighlightSequence(currentY + delta);
  }, [rotateY, startBorderHighlightSequence]);

  // ---- Drag handling ----
  const handleDragStart = (x, y) => { dragStart.current = { x, y }; };
  const handleDragEnd = (x, y) => {
    const dx = x - dragStart.current.x;
    const dy = y - dragStart.current.y;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
    const currentY = ((rotateY % 360) + 360) % 360;
    if (dx > 0) {
      const currentFace = getActiveFaceFromRotation(currentY);
      if (currentFace === 'front') return;
      startBorderHighlightSequence(currentY - 90);
    } else {
      if (Math.abs(currentY - 270) < 10 || (currentY < 10 && Math.abs(currentY - 0) < 10)) {
        const currentFace = getActiveFaceFromRotation(currentY);
        if (currentFace === 'left') return;
      }
      startBorderHighlightSequence(currentY + 90);
    }
  };

  const onMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
    const onMouseUp = (ev) => {
      handleDragEnd(ev.clientX, ev.clientY);
      window.removeEventListener('mouseup', onMouseUp);
    };
    window.addEventListener('mouseup', onMouseUp);
  };

  const onTouchStart = (e) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };
  const onTouchEnd = (e) => {
    const touch = e.changedTouches[0];
    handleDragEnd(touch.clientX, touch.clientY);
  };

  // ---- Sign out ----
  const handleSignOut = useCallback(async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
    }
  }, [auth, navigate]);

  // ---- Blink animation logic ----
  const canBlinkLeft = FACE_ORDER.indexOf(activeFace) > 0;
  const canBlinkRight = FACE_ORDER.indexOf(activeFace) < 3;

  useEffect(() => {
    if (blinkStartTimeoutRef.current) clearTimeout(blinkStartTimeoutRef.current);
    if (blinkSequenceRef.current) clearTimeout(blinkSequenceRef.current);
    setBlinkSide('none');
    setPulseColor('white');

    if (!rotationInProgress) {
      blinkStartTimeoutRef.current = setTimeout(() => {
        const bothNavigable = canBlinkLeft && canBlinkRight;
        if (!canBlinkLeft && !canBlinkRight) {
          setBlinkSide('none');
          return;
        }
        if (!bothNavigable) {
          setBlinkSide(canBlinkLeft ? 'left' : 'right');
          setPulseColor('white');
          return;
        }

        let currentSide = 'left';
        setBlinkSide(currentSide);
        setPulseColor('white');

        const runCycle = (side) => {
          setBlinkSide(side);
          setPulseColor('white');
          const whiteTimeout = setTimeout(() => setPulseColor('blue'), WHITE_HOLD_MS);
          const blueTimeout = setTimeout(() => {
            const nextSide = side === 'left' ? 'right' : 'left';
            runCycle(nextSide);
          }, WHITE_HOLD_MS + BLUE_HOLD_MS);
          blinkSequenceRef.current = blueTimeout;
          return () => {
            clearTimeout(whiteTimeout);
            clearTimeout(blueTimeout);
          };
        };

        const cleanup = runCycle(currentSide);
        return cleanup;
      }, POST_ROTATION_BLINK_DELAY);
    }

    return () => {
      if (blinkStartTimeoutRef.current) clearTimeout(blinkStartTimeoutRef.current);
      if (blinkSequenceRef.current) clearTimeout(blinkSequenceRef.current);
    };
  }, [rotationInProgress, canBlinkLeft, canBlinkRight]);

  useEffect(() => {
    return () => {
      if (chevronFadeInTimeoutRef.current) clearTimeout(chevronFadeInTimeoutRef.current);
      if (blinkSequenceRef.current) clearTimeout(blinkSequenceRef.current);
    };
  }, []);

  // ---- Keyboard navigation (left / right arrow keys) ----
  useEffect(() => {
    if (!ENABLE_KEYBOARD_NAVIGATION) return;
    const handleKeyDown = (e) => {
      // Ignore if a modal is open or a rotation is already in progress
      if (isTransitioning || isConversionModalOpen || isSlide3InfoOpen) return;

      const currentIndex = FACE_ORDER.indexOf(activeFace);
      if (e.key === 'ArrowLeft' && currentIndex > 0) {
        e.preventDefault();
        navigateToFace(currentIndex - 1);
      } else if (e.key === 'ArrowRight' && currentIndex < FACE_ORDER.length - 1) {
        e.preventDefault();
        navigateToFace(currentIndex + 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeFace, isTransitioning, isConversionModalOpen, isSlide3InfoOpen, navigateToFace]);

  // ---- Early return while loading or no user ----
  if (isLoading) {
    return (
      <div style={{ height: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#2D2D2D' }}>
        <p style={{ color: 'white', fontFamily: 'avenir-lt-pro, sans-serif' }}>Loading...</p>
      </div>
    );
  }

  if (!authUser) {
    return null;
  }

  // ---- Values for Slide 3 info popup ----
  const iterationsForImages = bundle.images * ITERATIONS_PER_FINAL_IMAGE;
  const imageValue = bundle.images * IMAGE_MARKET_RATE;
  const videoValueLow = bundle.videos * VIDEO_MARKET_RATE_LOW;
  const videoValueHigh = bundle.videos * VIDEO_MARKET_RATE_HIGH;

  // ---- Render ----
  const faceSize = cubeSize;
  const depth = cubeSize;
  if (faceSize === 0) return null;

  const halfS = faceSize / 2;
  const halfD = depth / 2;
  const currentSlideIndex = FACE_ORDER.indexOf(activeFace);
  const isLastSlide = currentSlideIndex === 3;
  const overlayMaxWidth = isMobile ? '280px' : '480px';
  const wallColor = '#2a2a2a';
  const wallBorder = '2px solid #4e4e4eff';

  const logoSize = isMobile ? 30 : 40;
  const avatarSize = isMobile ? 40 : 40;
  const stackGap = isMobile ? 20 : 15;

  const gridOverlayBase = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage: `
      linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px),
      linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)
    `,
    backgroundSize: '40px 40px',
    backgroundPosition: '0 0',
    animation: 'gridMove 8s linear infinite',
    pointerEvents: 'none',
    transition: 'opacity 0.6s ease',
  };

  const currentPulseColor = pulseColor === 'blue' ? PULSE_COLOR_BLUE : PULSE_COLOR_WHITE;

  const ArrowLeft = ({ strokeColor }) => (
    <svg width="100%" height="100%" viewBox="0 0 40 40" style={{ display: 'block' }}>
      <path d="M25 10 L15 20 L25 30" fill="none" stroke={strokeColor} strokeWidth="1.5"
            strokeLinecap="square" strokeLinejoin="square" style={{ transition: 'stroke 0.5s ease' }} />
    </svg>
  );

  const ArrowRight = ({ strokeColor }) => (
    <svg width="100%" height="100%" viewBox="0 0 40 40" style={{ display: 'block' }}>
      <path d="M15 10 L25 20 L15 30" fill="none" stroke={strokeColor} strokeWidth="1.5"
            strokeLinecap="square" strokeLinejoin="square" style={{ transition: 'stroke 0.5s ease' }} />
    </svg>
  );

  const arrowFace = rotationInProgress ? targetFace : activeFace;
  const arrowFaceIndex = FACE_ORDER.indexOf(arrowFace);
  const canBlinkLeftArrow = arrowFaceIndex > 0;
  const canBlinkRightArrow = arrowFaceIndex < 3;
  const activeRotationArrow = FACE_ROTATIONS[arrowFace];

  const wallData = FACE_ORDER.map((face) => {
    const faceRotation = FACE_ROTATIONS[face];
    const diff = ((faceRotation - activeRotationArrow) + 360) % 360;
    const isLeftWall = diff === 270;
    const isRightWall = diff === 90;
    const navigatePossible = (isLeftWall && canBlinkLeftArrow) || (isRightWall && canBlinkRightArrow);
    const arrowDir = isLeftWall ? 'left' : 'right';
    const faceIndex = FACE_ORDER.indexOf(face);
    const isActive = activeFace === face;
    return { face, isLeftWall, isRightWall, navigatePossible, arrowDir, faceIndex, isActive };
  });

  return (
    <div style={{ position: 'relative', height: '100dvh', overflow: 'hidden', background: '#2D2D2D' }}>
      {/* Header Overlay */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 30,
        padding: isMobile ? '16px 16px' : '20px 32px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        background: 'transparent', pointerEvents: 'auto',
        opacity: isConversionModalOpen || isSlide3InfoOpen ? 0 : 1, transition: 'opacity 0.3s ease',
      }}>
        <button
          onClick={handleSignOut}
          style={{
            width: isMobile ? '36px' : '44px', height: isMobile ? '36px' : '44px',
            borderRadius: '50%', background: 'rgba(89, 89, 89, 0.8)',
            backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.2)',
            color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center',
            justifyContent: 'center', transition: 'all 0.2s ease', padding: 0,
            flexShrink: 0, marginLeft: isMobile ? '8px' : '0px',
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#cc0000'}
          onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(89, 89, 89, 0.8)'}
          aria-label="Sign Out"
        >
          <GiPowerButton size={isMobile ? 18 : 22} />
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: stackGap, flexShrink: 0 }}>
          <img src={LogoWhite} alt="Outlne Logo" style={{
            width: logoSize, height: logoSize, objectFit: 'contain',
            transform: `translateX(${LOGO_X_OFFSET}px) translateY(${isMobile ? 5 : 0}px)`,
          }} />
          <div style={{
            width: avatarSize, height: avatarSize, borderRadius: '50%', border: '2px solid #88b7ff',
            overflow: 'hidden', background: '#595959', display: 'flex', alignItems: 'center',
            justifyContent: 'center', transform: `translateX(${AVATAR_X_OFFSET}px) translateY(${isMobile ? 7 : 0}px)`,
          }}>
            <img src={avatarUrl} alt="User" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        </div>
      </div>

      {/* Interactive 3D Box Area – now full screen */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        background: '#2D2D2D', zIndex: 1, overflow: 'hidden',
      }}>
        <div style={{
  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10,
  pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'flex-start',
  flexDirection: 'column', paddingTop: activeFace === 'back' ? (isMobile ? '65%' : '35%') : (isMobile ? '70%' : '35%'),
  opacity: isConversionModalOpen || isSlide3InfoOpen ? 0 : 1, transition: 'opacity 0.3s ease',
}}>
          <div style={{ width: overlayMaxWidth, maxWidth: '90vw', textAlign: 'center' }}>
            {/* Slide 1: Welcome */}
            {activeFace === 'front' && (
              <div style={{ animation: 'fadeIn 0.5s ease' }}>
                <h2 style={{
                  fontFamily: 'didot, sans-serif', fontStyle: 'italic',
                  fontSize: isMobile ? '1.8rem' : '2.5rem', fontWeight: 400,
                  color: '#ffffff', margin: 0, marginBottom: '1rem', letterSpacing: '-0.5px',
                }}>
                  Welcome, {displayName}
                </h2>
                <p style={{
                  color: '#ffffff', fontSize: isMobile ? '0.85rem' : '0.95rem',
                  lineHeight: 1.6, margin: 0, fontFamily: 'avenir-lt-pro, sans-serif', opacity: 0.9,
                }}>
                  Your <strong style={{ color: '#ffffff' }}>{billingLabel}</strong>{' '}
                  <strong style={{ color: '#88b7ff' }}>{planName}</strong> plan gives you
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', margin: '12px 0' }}>
                  <div style={{
                    background: '#595959', borderRadius: '20px', padding: '6px 16px',
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                    whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
                  }}>
                    <RiImageCircleAiLine size={16} style={{ flexShrink: 0 }} />
                    <span>{iterationCap.toLocaleString()} iterations</span>
                  </div>
                  <span style={{ color: '#aaa', fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: 500 }}>or</span>
                  <div style={{
                    background: '#595959', borderRadius: '20px', padding: '6px 16px',
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                    whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
                  }}>
                    <RiVideoOnAiLine size={16} style={{ flexShrink: 0 }} />
                    <span>{videoCap.toLocaleString()} videos</span>
                  </div>
                </div>
                <p style={{
                  color: '#aaa', fontSize: '0.7rem', lineHeight: 1.4, margin: '4px 0 0 0',
                  fontFamily: 'avenir-lt-pro, sans-serif',
                }}>
                </p>
              </div>
            )}

            {/* Slide 2: Real World Value */}
            {activeFace === 'right' && (
              <div style={{ animation: 'fadeIn 0.5s ease' }}>
                <h2 style={{
                  fontFamily: 'avenir-lt-pro, sans-serif', fontSize: isMobile ? '1rem' : '1.2rem',
                  fontWeight: 900, color: '#ffffff', margin: 0, marginBottom: '1rem',
                  textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.3,
                }}>
                  REAL WORLD VALUE
                </h2>
                <p style={{
                  color: '#ffffff', fontSize: isMobile ? '0.8rem' : '0.9rem',
                  lineHeight: 1.6, margin: 0, fontFamily: 'avenir-lt-pro, sans-serif', opacity: 0.9,
                }}>
                  In the digital age of credits, how do you measure what your work is actually worth?
                </p>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '12px 0' }}>
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsConversionModalOpen(true); }}
                    style={{
                      background: '#474747ff', border: '1px solid #5e5e5eff', borderRadius: '20px',
                      padding: '6px 16px', display: 'inline-flex', alignItems: 'center', gap: '8px',
                      cursor: 'pointer', fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif',
                      color: 'white', pointerEvents: 'auto',
                    }}
                    aria-label="Conversion Info"
                  >
                    <FiInfo size={16} style={{ flexShrink: 0 }} />
                    <span>Conversion Value</span>
                  </button>
                </div>
                <p style={{
                  color: '#ffffff', fontSize: isMobile ? '0.8rem' : '0.9rem',
                  lineHeight: 1.6, margin: 0, fontFamily: 'avenir-lt-pro, sans-serif', opacity: 0.9,
                }}>
                  Your <strong style={{ color: '#88b7ff' }}>{planName}</strong> plan's value is based on what it actually costs to create finalized interior<br></br> images and video assets.
                </p>
              </div>
            )}

            {/* Slide 3: How it Works */}
            {activeFace === 'back' && (
              <div style={{ animation: 'fadeIn 0.5s ease' }}>
                <h2 style={{
                  fontFamily: 'avenir-lt-pro, sans-serif', fontSize: isMobile ? '1.2rem' : '1.4rem',
                  fontWeight: 900, color: '#ffffff', margin: 0, marginBottom: '1rem',
                  textTransform: 'uppercase', letterSpacing: '1px',
                }}>
                  HOW IT WORKS
                </h2>
                <div style={{
                  color: '#ffffff', fontSize: isMobile ? '0.8rem' : '0.9rem',
                  lineHeight: 1.6, margin: 0, fontFamily: 'avenir-lt-pro, sans-serif', opacity: 0.9,
                }}>
                  <p style={{ margin: '0 0 12px 0' }}>
                    On your dashboard, you'll see a running balance of your remaining assets and its dollar value.
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', margin: '16px 0 18px', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Iteration pill */}
                    <div style={{
                      background: '#595959', borderRadius: '20px', padding: '6px 16px',
                      display: 'inline-flex', alignItems: 'center', gap: '6px',
                      fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                      whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
                    }}>
                      <RiImageCircleAiLine size={16} style={{ flexShrink: 0 }} />
                      <div style={{ position: 'relative', display: 'inline-block', minWidth: '50px', height: '1.2em' }}>
                        <span style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: showDollarValue ? 0 : 1, transition: 'opacity 0.5s ease',
                        }}>
                          {iterationCap.toLocaleString()}
                        </span>
                        <span style={{
                          position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          opacity: showDollarValue ? 1 : 0, transition: 'opacity 0.5s ease',
                        }}>
                          ${(bundle.images * IMAGE_MARKET_RATE).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>

                    <span style={{ color: '#aaa', fontSize: '0.8rem', fontWeight: 500 }}>or</span>

                    {/* Video pill + info icon wrapper */}
                    <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <div style={{
                        background: '#595959', borderRadius: '20px', padding: '6px 16px',
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                        whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
                      }}>
                        <RiVideoOnAiLine size={16} style={{ flexShrink: 0 }} />
                        <div style={{ position: 'relative', display: 'inline-block', minWidth: '50px', height: '1.2em' }}>
                          <span style={{
                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: showDollarValue ? 0 : 1, transition: 'opacity 0.5s ease',
                          }}>
                            {videoCap.toLocaleString()}
                          </span>
                          <span style={{
                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            opacity: showDollarValue ? 1 : 0, transition: 'opacity 0.5s ease',
                          }}>
                            ${(bundle.videos * VIDEO_MARKET_RATE_HIGH).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); setIsSlide3InfoOpen(true); }}
                        style={{
                          background: 'transparent', border: 'none', color: 'white',
                          cursor: 'pointer', padding: '4px', marginLeft: '4px',
                          display: 'flex', alignItems: 'center',
                          pointerEvents: 'auto', opacity: 0.8,
                        }}
                        aria-label="Plan value summary"
                      >
                        <FiInfo size={14} />
                      </button>
                    </div>
                  </div>
                  <p style={{ margin: '0 0 12px 0' }}>
                    That's <strong style={{ color: '#88b7ff' }}>{planValueStr}</strong> in value every month.
                  </p>
                  <p style={{ margin: '0' }}>
                    Every generated interior iteration and rendered video updates your account's remaining balance.
                  </p>
                </div>
              </div>
            )}

            {/* Slide 4: Done */}
            {activeFace === 'left' && (
              <div style={{ animation: 'fadeIn 0.5s ease' }}>
                <h2 style={{
                  fontFamily: 'avenir-lt-pro, sans-serif', fontSize: isMobile ? '1rem' : '1.2rem',
                  fontWeight: 900, color: '#ffffff', margin: 0, marginBottom: '1rem',
                  textTransform: 'uppercase', letterSpacing: '1px', lineHeight: 1.3,
                }}>
                  Your Dashboard Awaits
                </h2>
                <p style={{
                  color: '#ffffff', fontSize: isMobile ? '0.8rem' : '0.9rem',
                  lineHeight: 1.6, margin: 0, fontFamily: 'avenir-lt-pro, sans-serif', opacity: 0.9,
                }}>
                  Cheers to designing beautiful spaces.<br /><br />
                  Built With Love.<br />
                  <span style={{ color: '#88b7ff' }}>- Outlne Team</span>
                </p>
              </div>
            )}
          </div>
        </div>

        {/* 3D Scene */}
        <div style={{
          position: 'absolute', top: '43%', left: '50%', width: faceSize, height: faceSize,
          transform: 'translate(-50%, -50%)', perspective: `${PERSPECTIVE}px`,
          touchAction: 'none', cursor: 'grab', background: 'transparent',
        }}
          onMouseDown={onMouseDown} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}
        >
          <div style={{
            position: 'absolute', top: '50%', left: '50%', width: 0, height: 0,
            transformStyle: 'preserve-3d',
            transform: `translateZ(0px) rotateY(${rotateY + rockOffset}deg)`,
            transition: isTransitioning ? `transform ${ROTATION_TRANSITION_DURATION}ms ease` : 'none',
          }}>
            {wallData.map(({ face, navigatePossible, arrowDir, faceIndex, isActive }) => {
              const isFlickering = flickeringFace === face;
              const isBorderHighlighted = borderHighlightFace === face;
              const faceTransforms = {
                front: `translateZ(-${halfD}px) rotateY(0deg)`,
                right: `translate3d(${halfS}px, 0, 0px) rotateY(-90deg)`,
                back: `translateZ(${halfD}px) rotateY(180deg)`,
                left: `translate3d(-${halfS}px, 0, 0px) rotateY(90deg)`,
              };
              const borderColor = isBorderHighlighted ? `2px solid ${BORDER_HIGHLIGHT_COLOR}` : wallBorder;
              const cursorStyle = navigatePossible ? 'pointer' : 'default';

              const handleClick = (e) => {
                e.stopPropagation();
                if (navigatePossible) navigateToFace(faceIndex);
              };

              const shouldShowChevron = navigatePossible && chevronsVisible && !isConversionModalOpen && !isSlide3InfoOpen;
              const isPulsing = shouldShowChevron && blinkSide === (isActive ? undefined : arrowDir);
              const chevronColor = isPulsing ? currentPulseColor : PULSE_COLOR_WHITE;

              return (
                <div key={face} onClick={handleClick} style={{
                  width: faceSize, height: faceSize, position: 'absolute',
                  top: -halfS, left: -halfS, transform: faceTransforms[face],
                  border: borderColor, background: wallColor, backfaceVisibility: 'hidden',
                  overflow: 'hidden', transition: 'border-color 0.2s ease', cursor: cursorStyle,
                }}>
                  <div style={{ ...gridOverlayBase, opacity: isActive ? 1 : 0 }} />
                  {isFlickering && (
                    <div style={{
                      position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                      backgroundColor: BORDER_HIGHLIGHT_COLOR,
                      backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.15) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.15) 1px, transparent 1px)`,
                      backgroundSize: '40px 40px', backgroundPosition: '0 0',
                      opacity: 0, pointerEvents: 'none',
                      animation: `flickerPulse ${FLICKER_OVERLAY_DURATION}ms ease-in-out`,
                    }} />
                  )}
                  <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    pointerEvents: 'none', opacity: shouldShowChevron ? 1 : 0,
                    transition: 'opacity 0.5s ease',
                  }}>
                    <div style={{ width: '30%', height: '30%' }}>
                      {arrowDir === 'right' ? <ArrowRight strokeColor={chevronColor} /> : <ArrowLeft strokeColor={chevronColor} />}
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Ceiling & Floor */}
            <div style={{
              width: faceSize, height: faceSize, position: 'absolute', top: -halfS, left: -halfS,
              transform: `translate3d(0, -${halfS}px, 0px) rotateX(-90deg)`,
              border: wallBorder, background: wallColor, backfaceVisibility: 'hidden', overflow: 'hidden',
            }} />
            <div style={{
              width: faceSize, height: faceSize, position: 'absolute', top: -halfS, left: -halfS,
              transform: `translate3d(0, ${halfS}px, 0px) rotateX(90deg)`,
              border: wallBorder, background: wallColor, backfaceVisibility: 'hidden', overflow: 'hidden',
            }} />
          </div>
        </div>
      </div>

      {/* Bottom Navigation Overlay – immersed into the 3D scene, white text, blue buttons */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        zIndex: 25, padding: '24px',
        paddingBottom: `calc(24px + env(safe-area-inset-bottom, 16px))`,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: '14px',
        background: 'transparent',
        pointerEvents: isConversionModalOpen || isSlide3InfoOpen ? 'none' : 'auto',
        opacity: isConversionModalOpen || isSlide3InfoOpen ? 0 : 1,
        transition: 'opacity 0.3s ease',
      }}>
        <p style={{
          color: 'rgba(255,255,255,0.7)', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.85rem',
          fontWeight: 500, margin: 0, textAlign: 'center',
        }}>
          Swipe or Tap to Navigate
        </p>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', justifyContent: 'center' }}>
          {FACE_ORDER.map((face, index) => (
            <button key={face} onClick={() => navigateToFace(index)} style={{
              width: currentSlideIndex === index ? '28px' : '12px', height: '12px',
              borderRadius: currentSlideIndex === index ? '12px' : '50%',
              background: currentSlideIndex === index ? '#88b7ff' : 'rgba(255,255,255,0.4)',
              border: 'none', cursor: 'pointer', transition: 'all 0.3s ease', padding: 0,
            }} aria-label={`Go to ${FACE_LABELS[face]}`} />
          ))}
        </div>
        <p style={{
          color: 'rgba(255,255,255,0.8)', fontFamily: 'avenir-lt-pro, sans-serif', fontSize: '0.75rem',
          fontWeight: 600, margin: 0, textTransform: 'uppercase', letterSpacing: '1px',
        }}>
          {FACE_LABELS[activeFace]}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', width: '100%', maxWidth: isMobile ? '100%' : '400px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ fontSize: '1.2rem', fontWeight: 600, color: '#88b7ff', fontFamily: 'avenir-lt-pro, sans-serif', lineHeight: 1 }}>
              {currentSlideIndex + 1}
            </span>
            <span style={{ fontSize: '1rem', color: 'rgba(255,255,255,0.6)', fontFamily: 'avenir-lt-pro, sans-serif', fontWeight: 400 }}>/ 4</span>
          </div>
          <button
            onClick={() => { if (isLastSlide) navigate('/dashboard'); }}
            disabled={!isLastSlide}
            style={{
              width: '100%', padding: isMobile ? '14px' : '16px',
              background: isLastSlide ? 'linear-gradient(135deg, #88b7ff, #6a9cff)' : 'rgba(255,255,255,0.15)',
              color: isLastSlide ? 'white' : 'rgba(255,255,255,0.6)',
              border: 'none', borderRadius: '30px',
              fontSize: isMobile ? '0.95rem' : '1.1rem', fontWeight: 600,
              cursor: isLastSlide ? 'pointer' : 'not-allowed', transition: 'all 0.3s ease',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
              fontFamily: 'avenir-lt-pro, sans-serif',
              opacity: isLastSlide ? 1 : 0.7,
              transform: isLastSlide ? 'scale(1)' : 'scale(0.98)',
              pointerEvents: isLastSlide ? 'auto' : 'none',
            }}
            onMouseEnter={(e) => { if (isLastSlide) e.target.style.transform = 'scale(1.02)'; }}
            onMouseLeave={(e) => { if (isLastSlide) e.target.style.transform = 'scale(1)'; }}
          >
            Proceed to Dashboard
          </button>
        </div>
      </div>

      {/* Conversion Modal – iteration‑first, transparent credits, shared pool, supplement‑friendly wording */}
      {isConversionModalOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 30,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(24, 24, 24, 0.85)', animation: 'modalFadeIn 0.3s ease', pointerEvents: 'auto',
        }}>
          <div style={{
            background: '#2a2a2a', border: '1px solid #65a3ffff', borderRadius: '12px',
            padding: '40px', maxWidth: isMobile ? '90%' : '440px', width: '100%',
            color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', position: 'relative',
            transform: isMobile ? 'scale(0.85)' : 'none',
            transformOrigin: 'center center',
          }}>
            <button onClick={() => setIsConversionModalOpen(false)} style={{
              position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none',
              color: 'white', fontSize: '28px', cursor: 'pointer', padding: '8px', lineHeight: 1, opacity: 0.9,
            }} aria-label="Close conversion info">&times;</button>

            <h3 style={{ margin: '0 0 20px', fontSize: '1.2rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1px' }}>
              Conversion Value
            </h3>

            {/* Top paragraph – iteration‑first, credits shown parenthetically */}
            <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <p style={{ margin: '0', fontSize: '0.9rem', textAlign:'center', lineHeight: 1.6 }}>
                Your <strong style={{ color: '#88b7ff' }}>{planName}</strong> plan gives you up to{' '}
                <strong>{iterationCap.toLocaleString()} image iterations</strong> ({planCredits.toLocaleString()} credits) per billing cycle.
                For example,<br></br> those credits can support a suggested output of<br></br>{' '}
                <strong>{bundle.videos} videos + {bundle.images} final images</strong>.
              </p>
            </div>

            {/* Two‑column table for cost breakdown, asset labels in yellow */}
            <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                    <th style={{ padding: '4px 4px' }}>Asset</th>
                    <th style={{ padding: '4px 4px' }}>Credits</th>
                    <th style={{ padding: '4px 4px' }}>Dollar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ padding: '4px 4px' }}>1 Image Iteration</td>
                    <td style={{ padding: '4px 4px' }}>{IMAGE_COST_CREDITS}</td>
                    <td style={{ padding: '4px 4px' }}>${perIterationCost}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 4px' }}>
                      1 Final Image{' '}
                      <span style={{ color: '#9aef5cff', fontWeight: 'bold' }}>(asset)</span>
                    </td>
                    <td style={{ padding: '4px 4px' }}>{IMAGE_COST_CREDITS * ITERATIONS_PER_FINAL_IMAGE}</td>
                    <td style={{ padding: '4px 4px' }}>${perFinalImageCost}</td>
                  </tr>
                  <tr>
                    <td style={{ padding: '4px 4px' }}>
                      1 Video{' '}
                      <span style={{ color: '#9aef5cff', fontWeight: 'bold' }}>(asset)</span>
                    </td>
                    <td style={{ padding: '4px 4px' }}>{VIDEO_COST_CREDITS}</td>
                    <td style={{ padding: '4px 4px' }}>${perVideoCost}</td>
                  </tr>
                </tbody>
              </table>
              <p style={{ fontSize: '0.7rem', opacity: 0.8, marginTop: '8px', lineHeight: 1.4 }}>
                Compared to typical industry costs ($35/image, $75–$150/video), your plan’s output can be a cost‑effective supplement for asset production costs.
              </p>
            </div>

            {/* Pill counters – static, “or” phrasing, no yellow border */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
              <div style={{
                background: '#595959', borderRadius: '20px', padding: '6px 16px',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
              }}>
                <RiImageCircleAiLine size={16} style={{ flexShrink: 0 }} />
                <span>{iterationCap.toLocaleString()} image iterations</span>
              </div>
              <span style={{ color: '#aaa', fontSize: '0.8rem', fontWeight: 500 }}>or</span>
              <div style={{
                background: '#595959', borderRadius: '20px', padding: '6px 16px',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
              }}>
                <RiVideoOnAiLine size={16} style={{ flexShrink: 0 }} />
                <span>{videoCap.toLocaleString()} videos</span>
              </div>
            </div>
            <p style={{
              color: '#aaa', fontSize: '0.7rem', lineHeight: 1.4, margin: '8px 0 0 0',
              fontFamily: 'avenir-lt-pro, sans-serif', textAlign: 'center',
            }}>
              These are maximums if all credits were spent on one{' '}
              <span style={{ color: '#9aef5cff', fontWeight: 'bold' }}>asset resource</span>.
              <br />Mix them freely to your needs.
            </p>
          </div>
        </div>
      )}

      {/* Slide 3 Info Popup: Plan Value Summary – tree restored, video remaining, clean credits, centered table */}
      {isSlide3InfoOpen && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 30,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(24, 24, 24, 0.85)', animation: 'modalFadeIn 0.3s ease', pointerEvents: 'auto',
        }}>
          <div style={{
            background: '#2a2a2a', border: '1px solid #65a3ffff', borderRadius: '12px',
            padding: '40px', maxWidth: isMobile ? '90%' : '440px', width: '100%',
            color: 'white', fontFamily: 'avenir-lt-pro, sans-serif', position: 'relative',
            transform: isMobile ? 'scale(0.85)' : 'none',
            transformOrigin: 'center center',
          }}>
            <button onClick={() => setIsSlide3InfoOpen(false)} style={{
              position: 'absolute', top: '8px', right: '8px', background: 'none', border: 'none',
              color: 'white', fontSize: '28px', cursor: 'pointer', padding: '8px', lineHeight: 1, opacity: 0.9,
            }} aria-label="Close plan value summary">&times;</button>

            <h3 style={{ margin: '0 0 20px', fontSize: '1.2rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '1px' }}>
              Plan Value Summary
            </h3>

            {/* Account snapshot with remaining counts */}
            <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <p style={{ margin: '0', fontSize: '0.9rem', textAlign:'center', lineHeight: 1.6 }}>
                Your <strong style={{ color: '#88b7ff' }}>{planName}</strong> account has{' '}
                <strong>{remainingIterations.toLocaleString()} image iterations <br></br></strong>{' '}
                or <strong>{remainingVideos.toLocaleString()} videos remaining</strong>.
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', lineHeight: 1.6, textAlign:'center', color: '#aaa' }}>
                Your plan’s credits: <strong>{planCredits.toLocaleString()} credits</strong>.
              </p>
            </div>

            {/* Pill counters with dollar toggle, joined by “or” */}
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
              <div style={{
                background: '#595959', borderRadius: '20px', padding: '6px 16px',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
              }}>
                <RiImageCircleAiLine size={16} style={{ flexShrink: 0 }} />
                <div style={{ position: 'relative', display: 'inline-block', minWidth: '50px', height: '1.2em' }}>
                  <span style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: popupShowDollar ? 0 : 1, transition: 'opacity 0.5s ease',
                  }}>
                    {iterationCap.toLocaleString()}
                  </span>
                  <span style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: popupShowDollar ? 1 : 0, transition: 'opacity 0.5s ease',
                  }}>
                    ${(bundle.images * IMAGE_MARKET_RATE).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
              <span style={{ color: '#aaa', fontSize: '0.8rem', fontWeight: 500 }}>or</span>
              <div style={{
                background: '#595959', borderRadius: '20px', padding: '6px 16px',
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                fontSize: '0.8rem', fontFamily: 'avenir-lt-pro, sans-serif', color: 'white',
                whiteSpace: 'nowrap', minWidth: '90px', justifyContent: 'center',
              }}>
                <RiVideoOnAiLine size={16} style={{ flexShrink: 0 }} />
                <div style={{ position: 'relative', display: 'inline-block', minWidth: '50px', height: '1.2em' }}>
                  <span style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: popupShowDollar ? 0 : 1, transition: 'opacity 0.5s ease',
                  }}>
                    {videoCap.toLocaleString()}
                  </span>
                  <span style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    opacity: popupShowDollar ? 1 : 0, transition: 'opacity 0.5s ease',
                  }}>
                    ${(bundle.videos * VIDEO_MARKET_RATE_HIGH).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>

            {/* Monthly creative range highlighted in blue */}
            <p style={{
              fontSize: '0.9rem', lineHeight: 1.6, margin: '0 0 20px', textAlign:'center',
              color: '#65a3ffff', fontWeight: 700
            }}>
              Your estimated monthly creative range<br></br> is the sum of the value counters above,<br></br> {planValueStr}
            </p>

            {/* Reference value breakdown container with tree */}
            <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '12px' }}>
              <p style={{ fontSize: '0.7rem', lineHeight: 1.6, textAlign:'center', margin: '0 0 12px', color: '#aaa' }}>
                Suggested output mix that fully uses your generation budget
              </p>

              {/* Tree diagram */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '16px' }}>
                {/* Root */}
                <div style={{
                  background: '#3a3a3a', border: '1px solid #65a3ff', borderRadius: '8px',
                  padding: '4px 12px', fontSize: '0.8rem', fontWeight: 600,
                  color: '#fff', marginBottom: '4px'
                }}>
                  {iterationCap} image iterations
                </div>
                {/* Vertical line down */}
                <div style={{ width: '1px', height: '12px', background: '#65a3ff' }} />
                {/* Horizontal line with two descending lines */}
                <div style={{ display: 'flex', width: '100%', justifyContent: 'center', position: 'relative', marginTop: '-1px' }}>
                  <div style={{ flex: 1, height: '1px', background: '#65a3ff' }} />
                  <div style={{ width: '1px', height: '12px', background: '#65a3ff' }} />
                  <div style={{ flex: 1, height: '1px', background: '#65a3ff' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', marginTop: '-1px' }}>
                  <div style={{ width: '1px', height: '12px', background: '#65a3ff', marginLeft: 'auto', marginRight: 'auto' }} />
                  <div style={{ width: '1px', height: '12px', background: '#65a3ff', marginLeft: 'auto', marginRight: 'auto' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-around', width: '100%' }}>
                  <div style={{
                    background: '#3a3a3a', border: '1px solid #65a3ff', borderRadius: '8px',
                    padding: '4px 10px', fontSize: '0.8rem', color: '#fff',
                    textAlign: 'center'
                  }}>
                    {bundle.images} final images
                  </div>
                  <div style={{
                    background: '#3a3a3a', border: '1px solid #65a3ff', borderRadius: '8px',
                    padding: '4px 10px', fontSize: '0.8rem', color: '#fff',
                    textAlign: 'center'
                  }}>
                    {bundle.videos} videos
                  </div>
                </div>
              </div>

              {/* Asset rates note */}
              <p style={{ fontSize: '0.7rem', opacity: 0.7, margin: '0 0 12px', textAlign: 'center', color: '#aaa' }}>
                Asset rates based on standard photo/video deliverable costs.
              </p>

              {/* Value breakdown table – centered with generous padding */}
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <thead>
                  <tr style={{ textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.2)' }}>
                    <th style={{ padding: '8px 12px' }}>Asset</th>
                    <th style={{ padding: '8px 12px' }}>Count</th>
                    <th style={{ padding: '8px 12px' }}>Asset Rate</th>
                    <th style={{ padding: '8px 12px' }}>Value (Low)</th>
                    <th style={{ padding: '8px 12px' }}>Value (High)</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ textAlign: 'center' }}>
                    <td style={{ padding: '8px 12px' }}>Final Images</td>
                    <td style={{ padding: '8px 12px' }}>{bundle.images}</td>
                    <td style={{ padding: '8px 12px' }}>$35 each</td>
                    <td style={{ padding: '8px 12px' }}>${imageValue.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px' }}>${imageValue.toLocaleString()}</td>
                  </tr>
                  <tr style={{ textAlign: 'center' }}>
                    <td style={{ padding: '8px 12px' }}>Videos</td>
                    <td style={{ padding: '8px 12px' }}>{bundle.videos}</td>
                    <td style={{ padding: '8px 12px' }}>$75 - $150 each</td>
                    <td style={{ padding: '8px 12px' }}>${videoValueLow.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px' }}>${videoValueHigh.toLocaleString()}</td>
                  </tr>
                  <tr style={{ textAlign: 'center', borderTop: '1px solid rgba(255,255,255,0.2)', fontWeight: 700 }}>
                    <td style={{ padding: '8px 12px' }}>Total</td>
                    <td style={{ padding: '8px 12px' }}></td>
                    <td style={{ padding: '8px 12px' }}></td>
                    <td style={{ padding: '8px 12px' }}>${planValueRange.low.toLocaleString()}</td>
                    <td style={{ padding: '8px 12px' }}>${planValueRange.high.toLocaleString()}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes gridMove {
          0% { background-position: 0 0; }
          100% { background-position: 40px 40px; }
        }
        @keyframes flickerPulse {
          0% { opacity: 0; }
          20% { opacity: 0.8; }
          80% { opacity: 0.8; }
          100% { opacity: 0; }
        }
        @keyframes modalFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}